<template>
  <div class="page-container-wrapper">
    <loader v-if="dataLoading"/>
    <template v-else>
      <div class="page-container-header d-flex align-items-center">
        <h2 class="text-md mb-0">{{ $t('message.Guideline.publications') }}</h2>
        <b-button variant="info" class="mr-2 ml-auto" @click="goBack">{{ $t('message.return') }}</b-button>
      </div>

      <div class="container-fluid">
        <div class="row">
          <!-- LEFT -->
          <div class="col-md-12 col-xl-7">
            <div class="page-container guidelines-editor" id="page-container">
              <b-alert v-show="dataError" class="mt-4" show variant="danger">{{ $t('message.connectionError') }}</b-alert>
              <div v-if="showActionButtons || showUploadedFiles" class="page-container-toolbar">
                <template v-if="showActionButtons">
                  <b-button variant="outline-primary" class="mr-2" @click="showApproveInfo">{{ $t("message.Guideline.preApprove") }}</b-button>
                  <b-button variant="outline-primary" @click="showAWNInfo">{{ $t("message.Guideline.additionalWorkNeeded") }}</b-button>
                </template>

                <template v-if="showUploadedFiles">
                  <router-link class="btn btn-outline-primary btn text-center" :to="'/guidelines/' + guideline.id + '/review'">
                    <template v-if="imReviewer(guideline) && guideline.phase == guidelinePhaseTypes.uploadedForReview">
                      {{ $t('message.Guideline.reviewGuideline') }}
                    </template>
                    <template v-else>
                      {{ $t('message.Guideline.showReviewButton') }}
                    </template>
                  </router-link>
                </template>
              </div>

              <div class="px-3">
                <template v-if="gpcFinalApprovalPhase">
                  <b-card :title="$t('message.Guideline.gpcFinalApprovalPhase')" class="mb-2">
                    <template v-if="guidelineFiles && guidelineFiles.length">
                      <div class="table-responsive">
                        <b-table striped hover :items="guidelineFiles"
                                 :fields="['size', {key: 'upload_id', label: $t('message.Guideline.uploadId')}, {key: 'name', label: $t('message.Guideline.file')}, 'type', 'actions']">
                          <template slot="info" slot-scope="data">
                            <pre>
                              {{ data.item.response.data.id }}
                            </pre>
                          </template>

                          <template slot="upload_id" slot-scope="data">
                            {{ $t("message.Guideline.fileVersion", {version: data.item.upload_id}) }}
                          </template>

                          <template slot="progress" slot-scope="data">
                            <b-progress :value="parseInt(data.item.progress)" :max="100" show-progress animated></b-progress>
                          </template>

                          <template slot="actions" slot-scope="data">
                            <a target="_blank" :href="data.item.url">
                              <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                            </a>
                          </template>
                        </b-table>
                      </div>
                    </template>

                    <p>
                      <button class="btn btn-outline-primary" @click="prepareZippedFiles(guideline.submitter_id, guideline.upload_id)">
                        {{ $t('message.Guideline.downloadZipped') }}
                      </button>
                    </p>

                    <p>
                      <b-button @click="gpcFinalApprovalApprovedInfo" variant="primary">{{ $t("message.Guideline.approved") }}</b-button>
                      <b-button @click="gpcFinalApprovalOnHoldInfo" variant="warning" class="ml-2">{{ $t("message.Guideline.putOnHold") }}</b-button>
                    </p>
                  </b-card>
                </template>

                <template v-if="boardApprovalPhase">
                  <b-card :title="$t('message.Guideline.boardApprovalPhase')" class="mb-2">
                    <template v-if="guidelineFiles && guidelineFiles.length">
                      <div class="table-responsive">
                        <b-table striped hover :items="guidelineFiles"
                                 :fields="['size', {key: 'upload_id', label: $t('message.Guideline.uploadId')}, {key: 'name', label: $t('message.Guideline.file')}, 'type', 'actions']">
                          <template slot="info" slot-scope="data">
                            <pre>
                              {{ data.item.response.data.id }}
                            </pre>
                          </template>

                          <template slot="upload_id" slot-scope="data">
                            {{ $t("message.Guideline.fileVersion", {version: data.item.upload_id}) }}
                          </template>

                          <template slot="progress" slot-scope="data">
                            <b-progress :value="parseInt(data.item.progress)" :max="100" show-progress animated></b-progress>
                          </template>

                          <template slot="actions" slot-scope="data">
                            <a target="_blank" :href="data.item.url">
                              <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                            </a>
                          </template>
                        </b-table>
                      </div>
                    </template>

                    <p>
                      <button class="btn btn-outline-secondary" @click="prepareZippedFiles(guideline.submitter_id, guideline.upload_id)">
                        {{ $t('message.Guideline.downloadZipped') }}
                      </button>
                    </p>

                    <p>
                      <b-button @click="boardApprovalApprovedInfo" variant="primary">{{ $t("message.Guideline.approved") }}</b-button>
                      <b-button @click="boardApprovalOnHoldInfo" variant="warning" class="ml-2">{{ $t("message.Guideline.putOnHold") }}</b-button>
                    </p>
                  </b-card>
                </template>

                <template v-if="isGuidelineSubmitter && guideline.phase === guidelinePhaseTypes.submitted">
                  <b-card :title="$t('message.Guideline.submittedInfo')" class="mb-2">
                    {{ $t('message.Guideline.ccReviewInfo') }}
                  </b-card>
                </template>

                <template v-if="isGuidelineSubmitter && guideline.phase === guidelinePhaseTypes.preApproved">
                  <b-card :title="$t('message.Guideline.uploadFilesInfo')" class="mb-2 bg-light">
                    <router-link class="btn btn-primary btn text-center" :to="'/guidelines/' + guideline.id + '/upload'">
                      {{ $t('message.Guideline.uploadFiles') }}
                    </router-link>
                  </b-card>
                </template>

                <template v-if="showUploadedFiles">
                  <b-card :title="$t('message.Guideline.uploadedForReviewFilesInfo')" class="mb-2">
                    <div class="table-responsive">
                      <b-table v-if="guidelineFiles && guidelineFiles.length > 0" striped hover :items="guidelineFiles"
                               :fields="[ 'user_name', {key: 'upload_id', label: $t('message.Guideline.uploadId')}, 'size', {key: 'name', label: $t('message.Guideline.file')}, 'type', 'actions']">
                        <template slot="info" slot-scope="data">
                      <pre>
                        {{ data.item.response.data.id }}
                      </pre>
                        </template>

                        <template slot="upload_id" slot-scope="data">
                          {{ $t("message.Guideline.fileVersion", {version: data.item.upload_id}) }}
                        </template>

                        <template slot="actions" slot-scope="data">
                          <a target="_blank" :href="data.item.url">
                            <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                          </a>

                          <a v-if="isSuperUser" @click.prevent="deleteFileInfo(data.item)">
                            <font-awesome-icon icon="trash" size="lg" class="mr-2 text-danger"/>
                          </a>
                        </template>
                      </b-table>
                    </div>
                  </b-card>
                </template>

                <template v-if="startBoardProcess">
                  <b-card :title="$t('message.Guideline.startBoardProcessInfo')" class="mb-2 bg-light">
                    <router-link class="btn btn-primary btn text-center" :to="'/guidelines/' + guideline.id + '/gpc-final-approval-process'">
                      {{ $t('message.Guideline.startBoardProcess') }}
                    </router-link>
                  </b-card>
                </template>

                <div class="row">
                  <div class="col-md-12">
                    <!-- LEFT BASE DATA -->
                    <base-data-left :guideline="guideline" class="d-xl-none"></base-data-left>

                    <!-- LEFT PROGRESS -->
                    <phase-preview-left :guideline="guideline" class="d-xl-none"></phase-preview-left>


                    <b-card :title="$t('message.Guideline.section2')" class="mb-2" v-if="showInvolvedParties==0">
                      <b-row class="my-1 px-3">
                        <b-col sm="6">
                          <label class="col-form-label">{{ $t('message.Guideline.submitter') }}:</label>
                        </b-col>
                        <b-col sm="6">
                          <label class="form-control-plaintext">{{ guideline.submitter_name }}
                          </label>
                        </b-col>
                      </b-row>

                      <b-row class="my-1 px-3">
                        <b-col sm="6">
                          <label class="col-form-label">{{ $t('message.Guideline.coAuthor') }}:</label>
                        </b-col>
                        <b-col sm="6">
                          <label class="form-control-plaintext">{{ guideline.co_author ? guideline.co_author.label : '' }}
                          </label>
                        </b-col>
                      </b-row>

                      <b-row class="my-1 px-3">
                        <b-col sm="6">
                          <label class="col-form-label">{{ $t('message.Guideline.coCoAuthor') }}:</label>
                        </b-col>
                        <b-col sm="6">
                          <label class="form-control-plaintext">
                            <span>{{ guideline.co_co_author.first_name }} {{ guideline.co_co_author.last_name }} &lt;{{ guideline.co_co_author.email }}&gt;</span>
                          </label>
                        </b-col>
                      </b-row>

                      <b-row class="my-1 px-3">
                        <b-col sm="6">
                          <label class="col-form-label">{{ $t('message.Guideline.coAuthors') }}:</label>
                        </b-col>

                        <b-col sm="6">
                          <label class="form-control-plaintext">
                            <span v-for="(author, key, index) in guideline.co_authors" :key="index">{{ author.first_name }} {{ author.last_name }} &lt;{{ author.email }}&gt;<template
                                v-if="key < guideline.co_authors.length - 1">, </template></span>

                            <p v-if="guideline.co_authors.length == 0">{{ $t('message.noChosen') }}</p>
                          </label>
                        </b-col>
                      </b-row>

                      <b-row class="my-1 px-3">
                        <b-col sm="6">
                          <label class="col-form-label">{{ $t('message.Guideline.exCoAuthors') }}:</label>
                        </b-col>

                        <b-col sm="6">
                          <label class="form-control-plaintext">
                            <span v-for="(author, key, index) in guideline.ex_co_authors" :key="index">{{ author.first_name }} {{ author.last_name }} ({{ author.society }}) <template
                                v-if="key < guideline.ex_co_authors.length - 1">, </template></span>

                            <p v-if="guideline.ex_co_authors.length == 0">{{ $t('message.noChosen') }}</p>
                          </label>
                        </b-col>
                      </b-row>

                      <b-row class="my-1 px-3">
                        <b-col sm="6">
                          <label class="col-form-label">{{ $t('message.Guideline.reviewUsers') }}:</label>

                        </b-col>
                        <b-col sm="6">
                          <label class="form-control-plaintext">
                            <comma-separated-span :list="guideline.review_user_ids" :field="'label'"></comma-separated-span>
                            <p v-if="guideline.review_user_ids.length == 0">{{ $t('message.noChosen') }}</p>
                          </label>
                        </b-col>
                      </b-row>

                      <b-button v-if="canModifyInvolvedParties" variant="outline-secondary" class="float-right" @click="showInvolvedParties=1">{{ $t('message.edit') }}</b-button>
                    </b-card>

                    <involved-parties v-if="canModifyInvolvedParties" :guideline="guideline" :active="showInvolvedParties" @hideInvolvedParties="showInvolvedParties=0"
                                      :external="true"></involved-parties>

                    <b-card :title="$t('message.Guideline.section3')" class="mb-2">

                      <b-row class="my-1 px-3">
                        <b-col sm="6">
                          <label class="col-form-label">{{ $t('message.Guideline.description') }}:</label>
                        </b-col>
                        <b-col sm="6">
                          <label class="form-control-plaintext">{{ guideline.description }}
                          </label>
                        </b-col>
                      </b-row>

                      <b-row class="my-1 px-3">
                        <b-col sm="6">
                          <label class="col-form-label">{{ $t('message.Guideline.modification') }}:</label>
                        </b-col>
                        <b-col sm="6">
                          <label class="form-control-plaintext">{{ guideline.changes }}
                          </label>
                        </b-col>
                      </b-row>

                      <milestones v-if="guidelineCreatedAt != null" :title="$t('message.Guideline.Milestones.estimatedTimeline')" :budgets="guidelineBudgets"
                                  :createdDate="guidelineCreatedAt"></milestones>
                      <milestones v-if="guidelineCreatedAt != null" :dates="guidelinePhasesToDates" :title="$t('message.Guideline.Milestones.timeline')" :budgets="guidelineBudgets"
                                  :createdDate="guidelineCreatedAt"></milestones>
                    </b-card>

                    <b-card :title="$t('message.Guideline.section4')" class="mb-2">
                      <b-row class="my-1 px-3" v-if="guideline.publication != null">
                        <b-col sm="6">
                          <label class="col-form-label">
                            {{ $t('message.Guideline.destinationsOfManuscript.' + guideline.publication) }}
                          </label>
                        </b-col>
                        <b-col sm="6">
                          <label class="form-control-plaintext" v-if="guideline.publication == 2">
                            {{ guideline.publication_target_other_journal }} /
                            {{ guideline.publication_target_other_journal_publisher }}
                          </label>
                          <label class="form-control-plaintext" v-else>
                            {{ $t('message.Guideline.publicationJournals.' + guideline.journals) }}
                          </label>
                        </b-col>
                      </b-row>
                      <b-row class="my-1 px-3" v-if="guideline.publication != null">
                        <b-col sm="6">
                          <label class="col-form-label">
                            {{ $t('message.Guideline.editorInChiefContacted') }}
                          </label>
                        </b-col>
                        <b-col sm="6">
                          <b-form-checkbox
                              v-model="guideline.editorinchief_contacted"
                              value="1"
                              unchecked-value="0"
                              disabled
                              data-vv-name="guideline.editorinchief_contacted"
                          >
                          </b-form-checkbox>
                        </b-col>
                      </b-row>
                    </b-card>

                    <budgets :guideline="guideline" @refresh-guideline="refreshGuideline($event)"></budgets>

                    <b-card v-if="parallelReviewFeedbackVisible" title="Parallel review feedback" class="mb-2">
                      <b-card title="Committee reviews" v-if="committeeReviews.length > 0">
                        <b-table striped hover :items="committeeReviews" :fields="['user_name', 'change_type', 'comment']">
                          <template slot="change_type" slot-scope="data">
                            {{ $t('message.Guideline.reviewType.change' + data.item.change_type) }}
                          </template>
                          <template slot="comment" slot-scope="data">
                            <template v-if="data.item.change_type == 0">
                              {{ $t('message.Guideline.noCommentEverythingok') }}
                            </template>
                            <template v-else>
                              <span class="preLine">{{ data.item.comment }}</span>
                            </template>
                          </template>
                        </b-table>
                      </b-card>

                      <b-card title="National delegates reviews" v-if="ndReviews.length > 0">
                        <b-table striped hover :items="ndReviews" :fields="['user_name', 'change_type', 'comment']">
                          <template slot="change_type" slot-scope="data">
                            {{ $t('message.Guideline.reviewType.change' + data.item.change_type) }}
                          </template>
                          <template slot="comment" slot-scope="data">
                            <template v-if="data.item.change_type == 0">
                              {{ $t('message.Guideline.noCommentEverythingok') }}
                            </template>
                            <template v-else>
                              <span class="preLine">{{ data.item.comment }}</span>
                            </template>
                          </template>
                        </b-table>
                      </b-card>
                    </b-card>

                    <b-card v-if="guideline.id != null && (isGuidelineSubmitter || isGpc || isSuperUser)" :title="$t('message.Comments.title')" class="mb-2">
                      <comments :guidelineId="guideline.id" :locked="commentsLocked"></comments>
                    </b-card>
                  </div>
                  <template v-if="showActionButtons">
                    <div class="page-container-toolbar bottom w-100">
                      <b-button variant="outline-primary" class="mr-2" @click="showApproveInfo">{{ $t("message.Guideline.preApprove") }}</b-button>
                      <b-button variant="outline-primary" @click="showAWNInfo">{{ $t("message.Guideline.additionalWorkNeeded") }}</b-button>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="d-md-none d-xl-block col-xl-5">
            <base-data-right :guideline="guideline"></base-data-right>

            <phase-preview :guideline="guideline"></phase-preview>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  GUIDELINE_REQUEST_BY_ID,
  GUIDELINE_CHANGE_PHASE,
  GUIDELINE_LIST_PHASES_BY_ID,
  GUIDELINE_FORMAL_CHECK_APPROVAL,
} from '@/store/actions/guidelines'
import {GUIDELINE_USER_ROLE_APPROVE} from '@/store/actions/guideline_user_role'
import {Guideline, GuidelinePhases} from '@/lib/guideline.js'

import {destinationsOfManuscript, kindsOfWork, typesOfManuscript} from '@/lib/selects.js'

import Milestones from '@/components/Milestones'

import InvolvedParties from '@/components/Guideline/GuidelineEditor/InvolvedParties'

import Comments from '@/components/Comments'
import Budgets from '@/components/Guideline/GuidelineView/Budgets'
import RoleAcceptance from '@/components/RoleAcceptance'
import CommaSeparatedSpan from '@/components/util/CommaSeparatedSpan'

import FileUpload from 'vue-upload-component/dist/vue-upload-component.part.js'

import BaseDataRight from '@/components/Guideline/BaseData/right.vue'
import BaseDataLeft from '@/components/Guideline/BaseData/left.vue'

import PhasePreview from '@/components/Guideline/PhasePreview'
import PhasePreviewLeft from '@/components/Guideline/PhasePreview/left.vue'
import Api from "@/services/Api";

export default {
  name: 'guidelines-view',
  components: {
    Milestones,
    Budgets,
    Comments,
    RoleAcceptance,
    CommaSeparatedSpan,
    FileUpload,
    BaseDataRight,
    BaseDataLeft,
    PhasePreview,
    PhasePreviewLeft,
    InvolvedParties
  },
  props: [],
  data() {
    return {
      showInvolvedParties: false,
      guideline: new Guideline,
      guidelinePhases: [],
      guidelinePhaseTypes: GuidelinePhases,
      typesOfManuscript: typesOfManuscript,
      kindsOfWork: kindsOfWork,
      destinationsOfManuscript: destinationsOfManuscript,

      files: [],
      editInolvedParties: true,
    }
  },
  computed: {
    gpcFinalApprovalPhase() {
      return this.isGpc && this.guideline.phase === this.guidelinePhaseTypes.gpcFinalApproval;
    },

    boardApprovalPhase() {
      return this.isIgo && this.guideline.phase == this.guidelinePhaseTypes.boardApproval;
    },

    guidelineBudgets() {
      return this.guideline.budget;
    },

    guidelineCreatedAt() {
      return this.guideline.created_at;
    },

    showActionButtons() {
      let hasFormalCheck = true;
      if (this.guideline.formalCheckApprovals) {
        hasFormalCheck = this.guideline.formalCheckApprovals.filter(elem => {
          return elem.user_id === this.getUserData.id && elem.revision === this.guideline.revision;
        }).length === 0;
      }

      return (this.isGpc || this.isSuperUser) && this.guideline.phase === this.guidelinePhaseTypes.submitted && hasFormalCheck;
    },

    commentsLocked() {
      if (this.isGpc || this.isSuperUser) {
        return false;
      }

      return this.guideline.phase >= this.guidelinePhaseTypes.preApproved;
    },

    // Read-only oversight of parallel review: GPC (and office) see the committee +
    // ND review feedback on the view page. Gated to those roles so committee/ND
    // reviewers do not see the other group's comments here.
    parallelReviewFeedbackVisible() {
      return this.guideline.review_mode === 'parallel'
        && (this.isGpc || this.isSuperUser)
        && (this.committeeReviews.length > 0 || this.ndReviews.length > 0);
    },

    committeeReviews() {
      if (this.guideline && this.guideline.reviews) {
        return this.guideline.reviews.filter(elem => elem.user_type === 0);
      }
      return [];
    },

    ndReviews() {
      if (this.guideline && this.guideline.reviews) {
        return this.guideline.reviews.filter(elem => elem.user_type === 1);
      }
      return [];
    },

    coAuthor() {
      let row = (this.guideline.co_author_ids.filter(elem => {
        return elem.value == this.getUserData.id
      })).pop();

      if (row != undefined) {
        return true;
      }

      return false;
    },

    uploadUrl() {
      return process.env.VUE_APP_API_URL + 'files/' + this.guideline.id;
    },

    uploadChunkedUrl() {
      return process.env.VUE_APP_API_URL + 'files-chunked/' + this.guideline.id;
    },

    chunk() {
      return {
        headers: {'Authorization': 'Bearer ' + this.getAuthToken},
        action: this.uploadChunkedUrl,
        maxActive: 1,
        maxRetries: 5,
      }
    },

    showFileSection() {
      return this.guideline.phase == this.guidelinePhaseTypes.preApproved
    },

    showUploadedFiles() {
      return [
        this.guidelinePhaseTypes.uploadedForReview,
        this.guidelinePhaseTypes.uploadReviewed,
        this.guidelinePhaseTypes.parallelReview,
        this.guidelinePhaseTypes.parallelReviewed,
        this.guidelinePhaseTypes.parallelReviewEnd,
      ].indexOf(this.guideline.phase) != -1
    },

    isGuidelineSubmitter() {
      return this.getUserData.id == this.guideline.submitter_id;
    },

    guidelineFiles() {
      if (this.guideline && this.guideline.files) {
        return this.guideline.files.filter(elem => {
          return elem.user_id == this.guideline.submitter_id && elem.upload_id == this.guideline.upload_id
        });
      }
    },

    startBoardProcess() {
      return this.isGuidelineSubmitter && this.guideline.phase == this.guidelinePhaseTypes.reviewByNationalDelegatesEnd;
    },

    canModifyInvolvedParties() {
      return this.isGpc && (this.guideline.phase == this.guidelinePhaseTypes.submitted);
    },

    guidelinePhasesToDates() {
      if (this.guidelinePhases.length < 1) {
        return [];
      }

      // copy the phase for showing DOI Forms due
      const index = this.guidelinePhases.findIndex((e) => e.phase === this.guidelinePhaseTypes.preApproved)
      if (index > -1) {
        this.guidelinePhases.splice(index, 0, this.guidelinePhases[index])
      }

      return this.guidelinePhases
          .map((phase, index) => {

            // TODO title
            let result = {
              symbol: "",
              date: this.moment(phase.updated_at).format('DD-MM-YYYY'),
              title: ""
            };

            switch (phase.phase) {
              case this.guidelinePhaseTypes.submitted:
                result.symbol = this.$t('message.Guideline.phase10')
                result.title = 'Entry submitted'
                break;
              case this.guidelinePhaseTypes.preApproved:
                result.symbol = this.$t('message.Guideline.phase40')
                result.title = 'Pre-approved entry'
                if (this.guidelinePhases.slice(0, index).findIndex((e) => e.phase === this.guidelinePhaseTypes.preApproved) > -1) {
                  result.symbol = this.$t('message.Guideline.Milestones.M3')
                  result.title = 'Involved parties defined'
                }
                break;

              case this.guidelinePhaseTypes.uploadedForGpcReview:
                result.symbol = this.$t('message.Guideline.Milestones.M4')
                result.title = 'Guideline draft uploaded'
                if (this.guidelinePhases.slice(0, index).findIndex((e) => e.phase === this.guidelinePhaseTypes.uploadedForGpcReview) > -1) {
                  result.symbol = this.$t('message.Guideline.Milestones.M4_4')
                  result.title = 'Edited version of Guideline uploaded'
                }
                break;

              case this.guidelinePhaseTypes.uploadGpcReviewed:
                result.symbol = this.$t('message.Guideline.Milestones.M4_2')
                result.title = 'GPC Review finished'
                break;

              case this.guidelinePhaseTypes.uploadedForReview:
                result.symbol = this.$t('message.Guideline.Milestones.M5')


                /* new phase instead of Milestone
                case this.guidelinePhaseTypes.uploadedForReview:
                  result.symbol = this.$t('message.Guideline.phase50')
                    */

                result.title = 'Guideline draft uploaded'
                if (this.guidelinePhases.slice(0, index).findIndex((e) => e.phase === this.guidelinePhaseTypes.uploadedForReview) > -1) {
                  result.symbol = this.$t('message.Guideline.phase50')
                  result.title = 'Edited version of Guideline uploaded'
                }
                break;
              case this.guidelinePhaseTypes.uploadReviewed:
                result.symbol = this.$t('message.Guideline.phase60')
                result.title = 'Review round #1 finished'
                break;
              case this.guidelinePhaseTypes.uploadReviewedEnd:
                result.symbol = this.$t('message.Guideline.phase70')
                result.title = 'Edited version approved (=review round #X)'
                if (this.guidelinePhases.slice(index + 1, this.guidelinePhases.length - 1).findIndex((e) => e.phase === this.guidelinePhaseTypes.uploadReviewedEnd) > -1) {
                  result.symbol = this.$t('message.Guideline.Milestones.M5')
                  result.title = 'Review round #1 finished'
                }
                break;
              case this.guidelinePhaseTypes.reviewByNationalDelegates:
                result.symbol = this.$t('message.Guideline.phase80')
                result.title = 'Guideline for National Delegate review uploaded'
                break;
              case this.guidelinePhaseTypes.reviewedByNationalDelegates:
                result.symbol = this.$t('message.Guideline.phase90')
                result.title = 'Edited guideline for National Delegate review uploaded'
                break;
              case this.guidelinePhaseTypes.reviewByNationalDelegatesEnd:
                result.symbol = this.$t('message.Guideline.phase100')
                result.title = 'Guideline approved'
                break;
              case this.guidelinePhaseTypes.boardApproval:
                result.symbol = this.$t('message.Guideline.phase110')
                result.title = 'Final manuscript uploaded'
                break;
              case this.guidelinePhaseTypes.boardApprovalApproved:
                result.symbol = this.$t('message.Guideline.phase120')
                result.title = 'Guideline finally approved'
                break;
              case this.guidelinePhaseTypes.finalPublish:
                result.symbol = this.$t('message.Guideline.phase140')
                result.title = 'Guideline published'
                break;

              default:
                return null;
            }

            return result;
          })
          .filter(e => e !== null);
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.$store.dispatch(GUIDELINE_REQUEST_BY_ID, this.$route.params.id).then((response) => {
        this.guideline = response.data.data;
      }).catch((error) => {
        this.handleUnauth(error.response.status);
      });

      this.$store.dispatch(GUIDELINE_LIST_PHASES_BY_ID, this.$route.params.id).then((response) => {
        this.guidelinePhases = response.data.data;
      }).catch((error) => {
        this.handleUnauth(error.response.status);
      });
    },

    refreshGuideline(guideline) {
      this.guideline = guideline;
    },

    goBack() {
      this.$router.push('/guidelines/')
    },

    showApproveInfo() {
      this.$swal({
        title: this.$t("message.areYouSure"),
        html: this.$t("message.Guideline.approveInfo"),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t("message.yesImSure"),
        cancelButtonText: this.$t("message.noCancel"),
      }).then((result) => {
        if (result.value) {
          this.preApproveFCA();
        }
      });
    },

    preApproveFCA() {
      this.$store.dispatch(GUIDELINE_FORMAL_CHECK_APPROVAL, {id: this.guideline.id, changeType: 0})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.yourFeedbackSubmitted"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {

        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    showAWNInfo() {
      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.additionalWorkInfo'),
        input: 'textarea',
        inputPlaceholder: this.$t('message.Guideline.rejectTextboxPlaceholder'),
        inputValidator: (value) => {
          return !value && this.$t('message.Guideline.youNeedToWrtiteSomething')
        },
        type: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('message.submit'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value != undefined) {
          this.revisionRequiredFCA(result.value);
        }
      });
    },

    revisionRequiredFCA(comment) {
      this.$store.dispatch(GUIDELINE_FORMAL_CHECK_APPROVAL, {id: this.guideline.id, changeType: 1, comment: comment})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.yourFeedbackSubmitted"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    approveUserRoleInfo(row) {
      let roleToChange = this.$t('message.Guideline.RoleAcceptance.roles.' + row.change_request)

      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.approveUserRoleInfo', {username: row.label, role: roleToChange}),
        type: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('message.yes'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value) {
          this.approveUserRole(row);
        }
      });
    },

    approveUserRole(row) {
      this.$store.dispatch(GUIDELINE_USER_ROLE_APPROVE, {guideline: this.guideline, role: row}).then(() => {
        this.$swal({
          title: this.$t("message.success"),
          text: this.$t("message.Guideline.RoleAcceptance.roleWasAccepted"),
          type: 'success',
        });

        this.getData();
      }).catch((error) => {
        this.handleUnauth(error.response.status);
      });
    },

    startUpload() {
      this.$refs.upload.active = true;
    },

    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Before adding a file
        // 添加文件前
        // Filter system files or hide files
        // 过滤系统文件 和隐藏文件
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent()
        }
        // Filter php html js file
        // 过滤 php html js 文件
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
          return prevent()
        }
      }
    },

    gpcFinalApprovalApprovedInfo() {
      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.boardApprovalApprovedInfo'),
        type: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('message.yes'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value) {
          this.gpcFinalApprovalApproved();
        }
      });
    },

    gpcFinalApprovalApproved() {
      this.$store.dispatch(GUIDELINE_CHANGE_PHASE, {id: this.guideline.id, phase: this.guidelinePhaseTypes.boardApproval})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.successfullyGpcFinalApproved"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    boardApprovalApprovedInfo() {
      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.boardApprovalApprovedInfo'),
        type: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('message.yes'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value) {
          this.boardApprovalApproved();
        }
      });
    },

    boardApprovalApproved() {
      this.$store.dispatch(GUIDELINE_CHANGE_PHASE, {id: this.guideline.id, phase: this.guidelinePhaseTypes.boardApprovalApproved})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.successfullyboardApproved"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    gpcFinalApprovalOnHoldInfo() {
      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.boardApprovalonHoldInfo'),
        input: 'textarea',
        inputPlaceholder: this.$t('message.Guideline.rejectTextboxPlaceholder'),
        inputValidator: (value) => {
          return !value && this.$t('message.Guideline.youNeedToWrtiteSomething')
        },
        type: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('message.submit'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value) {
          this.gpcFinalApprovalOnHold(result.value);
        }
      });
    },


    showOnHoldInfo() {
      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.additionalWorkInfo'),
        input: 'textarea',
        inputPlaceholder: this.$t('message.Guideline.rejectTextboxPlaceholder'),
        inputValidator: (value) => {
          return !value && this.$t('message.Guideline.youNeedToWrtiteSomething')
        },
        type: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('message.submit'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value != undefined) {
          this.revisionRequiredFCA(result.value);
        }
      });
    },

    boardApprovalOnHoldInfo() {
      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.boardApprovalonHoldInfo'),
        input: 'textarea',
        inputPlaceholder: this.$t('message.Guideline.rejectTextboxPlaceholder'),
        inputValidator: (value) => {
          return !value && this.$t('message.Guideline.youNeedToWrtiteSomething')
        },
        type: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('message.submit'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value) {
          this.boardApprovalonHold(result.value);
        }
      });
    },

    gpcFinalApprovalOnHold(comment) {
      this.$store.dispatch(GUIDELINE_CHANGE_PHASE, {id: this.guideline.id, phase: this.guidelinePhaseTypes.gpcFinalHold, type: 2, comment: comment})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.successfullyPuttedOnHold"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    boardApprovalonHold(comment) {
      this.$store.dispatch(GUIDELINE_CHANGE_PHASE, {id: this.guideline.id, phase: this.guidelinePhaseTypes.boardApprovalOnHold, type: 1, comment: comment})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.successfullyPuttedOnHold"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    getZipped() {
      window.open(this.guideline.zipUrl);
    },


    deleteFileInfo(file, fileIndex) {
      let fileId = file.response != undefined ? file.response.data.id : file.id;

      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.deleteFileInfo'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('message.yesImSure'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value != undefined) {
          this.deleteFile(fileId, fileIndex);
        }
      });
    },

    deleteFile(fileId) {
      this.$store.dispatch(GUIDELINE_DELETE_FILE_BY_ID, {gid: this.guideline.id, fid: fileId})
          .then(() => {
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
      });
    },

    prepareZippedFiles(userId, uploadId) {
      // Set as downloaded for user
      let files = this.guideline.files.filter(row => {return row.user_id === userId && row.upload_id === uploadId})

      for (let x in files) {
        files[x].downloaded = true
      }

      Api().get(`/guidelines/prepare-zipped/${this.guideline.id}/${userId}/${uploadId}`).then( response => {
        window.open(response.data.fileUrl);
      }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },
  }
}
</script>

<style lang="scss">
.guidelines-view {
}

.dragActive {
  background: white;
}

.dropzone {
  background: #e6e6e6;
  text-align: center;
  padding: 30px;
  border: 2px dashed;

  div {
    padding: 20px;
  }
}

.col-form-label {
  text-align: left;
}

.file-uploads {
  overflow: hidden;
  position: relative;
  text-align: center;
  display: inline-block;
}

.file-uploads.file-uploads-html4 {
  input[type="file"] {
    opacity: 0;
    font-size: 20em;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    width: 100%;
    height: 100%;
  }
}

.file-uploads.file-uploads-html5 {
  input[type="file"] {
    overflow: hidden;
    position: fixed;
    width: 1px;
    height: 1px;
    z-index: -1;
    opacity: 0;
  }
}
</style>

